import { FetchError, isFetchError } from '@/utility/reactToolkit';

/**
 * The result of mapping an RTK Query mutation error to something we can show
 * the user. Returned by {@link mapApiError}.
 *
 * - `message` is the localized, user-facing string (or `null` if there is no
 *   meaningful error to display, e.g. an aborted request).
 * - `traceId` is the backend trace-id from the FetchError payload, surfaced
 *   so support can correlate to NewRelic. `null` when not available.
 * - `code` is the raw backend code (`error.data.code`), preserved for callers
 *   that want to branch on it (e.g. navigate to a recovery flow).
 * - `isKnown` is `true` when the error code matched the {@link knownCodeMessages}
 *   registry. Callers can use this to decide whether to inline the message on
 *   a specific field or fall back to the generic toast-only treatment.
 */
export interface ApiErrorInfo {
  readonly message: string | null;
  readonly traceId: string | null;
  readonly code: string | null;
  readonly isKnown: boolean;
}

/**
 * Translation keys for backend error codes we want to localize specifically.
 *
 * To add a new mapping:
 *   1. Add the code → translation-key entry below.
 *   2. Add the matching key to `src/locales/{en,pt,es}.ts`.
 *
 * Anything not in this registry falls back to `AN_ERROR_OCCURRED`. Keep this
 * list focused on codes the backend reliably returns and that the user can
 * act on (retry later, change inputs, etc.).
 *
 * Codes are defined in `caliza-core-api` via `@ExceptionMapping(errorCode = …)`
 * — search there to confirm a code exists before adding a mapping.
 */
export const PAYMENT_APPROVAL_NO_ELIGIBLE_APPROVER_CODE = 'payment-approval.no-eligible-approver';

const knownCodeMessages: Readonly<Record<string, string>> = {
  // BRL out-of-market-hours (TransferFlow used to hard-code this).
  'transaction.out_of_market_hours': 'BRL_MARKET_HOURS_ALERT',

  [PAYMENT_APPROVAL_NO_ELIGIBLE_APPROVER_CODE]: 'API_ERROR_PAYMENT_APPROVAL_NO_ELIGIBLE_APPROVER',

  // No minimum configured for the corridor — generic copy (there's no value to
  // surface). The min/max *limit* errors (`simulation.minimum_amount` /
  // `simulation.maximum_amount`) are handled in templatedCodeMessages, where we
  // lift the actual limit out of the message.
  'transaction.minimum_amount_not_found': 'API_ERROR_TRANSACTION_MINIMUM_NOT_CONFIGURED',

  // Server-side insufficient-balance check (post client-side guard).
  'ledger_adjust.insufficient_source_balance': 'API_ERROR_INSUFFICIENT_BALANCE',

  // Simulation rejected because the corridor (currency/rail/destination
  // combination) isn't enabled — e.g. USD SWIFT to a country we don't serve.
  'flow_of_funds.corridor-not-available': 'API_ERROR_CORRIDOR_NOT_AVAILABLE',

  // Recipient validation rejections.
  'recipient.crypto_not_approved': 'API_ERROR_RECIPIENT_CRYPTO_NOT_APPROVED',
  'recipient.validation_error': 'API_ERROR_RECIPIENT_VALIDATION',
  'recipient.not_found': 'API_ERROR_RECIPIENT_NOT_FOUND',
  'recipient.under_review': 'API_ERROR_RECIPIENT_UNDER_REVIEW',

  // Travel Rule ownership proof rejected by Notabene (signature doesn't match
  // the recipient's wallet address). The other travel-rule.* request-shape
  // codes (recipient-id/type/proof/attestation required) can't be produced by
  // our UI — they keep the generic fallback on purpose.
  'travel-rule.proof_invalid': 'API_ERROR_TRAVEL_RULE_PROOF_INVALID',

  // Backend rejects simulation/transaction creation for a self-hosted wallet
  // without a valid ownership proof. Our gate normally prevents reaching this
  // (banner + blocked confirm), but simulations are created as soon as amounts
  // are typed — and the frontend-flag-off + backend-flag-on mismatch lands
  // here too.
  'travel-rule.ownership_proof_required': 'API_ERROR_TRAVEL_RULE_OWNERSHIP_PROOF_REQUIRED',
};

/**
 * Backend codes whose localized copy needs a value lifted out of the raw
 * `data.message`. The backend (`me.alidg.errors`) exposes the value via
 * `@ExposeAsArg`, but our FetchError shape only carries the already-rendered
 * `message` string — so we recover the value with a regex instead.
 *
 * `argPattern` must have exactly one capture group. When it matches, the
 * captured value is passed to `tr(messageKey, value)` (interpolated into the
 * `{0}` placeholder). When it doesn't match, we fall back to `fallbackKey`
 * (a generic, value-less copy) so the user still gets something actionable.
 *
 * To add a new mapping:
 *   1. Add the entry below.
 *   2. Add `messageKey` (with a `{0}`) and `fallbackKey` to the locales.
 */
interface TemplatedCodeMessage {
  readonly messageKey: string;
  readonly fallbackKey: string;
  readonly argPattern: RegExp;
}

const templatedCodeMessages: Readonly<Record<string, TemplatedCodeMessage>> = {
  // Transaction below the per-rail/currency minimum. Backend message reads
  // "Minimum amount for transaction/payment is 25.00" — we pull the trailing
  // number out so the user is told exactly what the minimum is.
  'flow_of_funds.invalid-amount': {
    messageKey: 'API_ERROR_TRANSACTION_MINIMUM_AMOUNT',
    fallbackKey: 'API_ERROR_SIMULATION_MINIMUM_AMOUNT',
    argPattern: /([\d,.]+)\s*$/,
  },

  // Provider per-corridor limits (Bitso/Cainvest). Backend messages read
  // "The transaction amount is below the minimum of 5 USDC" / "...above the
  // maximum of 1000000 MXN" — we pull the trailing "<value> <currency>" out so
  // the user is told the exact limit. Falls back to the value-less copy if the
  // wording ever changes.
  'simulation.minimum_amount': {
    messageKey: 'API_ERROR_SIMULATION_MINIMUM_AMOUNT_VALUE',
    fallbackKey: 'API_ERROR_SIMULATION_MINIMUM_AMOUNT',
    argPattern: /of\s+([\d.,]+\s+\S+)\s*$/,
  },
  'simulation.maximum_amount': {
    messageKey: 'API_ERROR_SIMULATION_MAXIMUM_AMOUNT_VALUE',
    fallbackKey: 'API_ERROR_SIMULATION_MAXIMUM_AMOUNT',
    argPattern: /of\s+([\d.,]+\s+\S+)\s*$/,
  },
};

const isString = (value: unknown): value is string => typeof value === 'string';

/**
 * Translation function shape — kept structural so callers can pass
 * either the real `useTranslator` result or a stub in tests, without
 * pulling React into this file.
 */
type Translate = (key: string, ...args: ReadonlyArray<string | number>) => string;

/**
 * Map an unknown error (typically `RTKMutation.error`) to user-facing info.
 *
 * Treats aborted requests as "no error to show" (returns `message: null`).
 * Everything else gets either the mapped message for its `data.code` or the
 * generic `AN_ERROR_OCCURRED` fallback.
 */
export const mapApiError = (error: unknown, tr: Translate): ApiErrorInfo => {
  if (error === null || error === undefined) {
    return EMPTY_API_ERROR;
  }

  if (isAbortLike(error)) {
    return EMPTY_API_ERROR;
  }

  if (!isFetchError(error)) {
    return {
      message: tr('AN_ERROR_OCCURRED'),
      traceId: null,
      code: null,
      isKnown: false,
    };
  }

  const { data } = error;
  const code = isString(data.code) ? data.code : null;
  const traceId = isString(data['trace-id']) ? data['trace-id'] : null;

  const knownKey = code !== null ? knownCodeMessages[code] : undefined;
  if (knownKey) {
    return {
      message: tr(knownKey),
      traceId: traceId,
      code: code,
      isKnown: true,
    };
  }

  const templated = code !== null ? templatedCodeMessages[code] : undefined;
  if (templated) {
    const arg = isString(data.message) ? data.message.match(templated.argPattern)?.[1] : undefined;
    return {
      message: arg ? tr(templated.messageKey, arg) : tr(templated.fallbackKey),
      traceId: traceId,
      code: code,
      isKnown: true,
    };
  }

  return {
    message: tr('AN_ERROR_OCCURRED'),
    traceId: traceId,
    code: code,
    isKnown: false,
  };
};

/**
 * Compose the toast text we show on rejection.
 *
 * We only append the trace-id when the error wasn't recognized. The thinking:
 * if we have a localized, actionable message ("BRL market is closed…"), the
 * user knows what to do and the trace-id is just noise. But if all we can
 * tell them is "an unknown error occurred", they have nothing to act on —
 * the trace-id is the only useful breadcrumb for support to follow.
 */
export const formatToastMessage = (info: ApiErrorInfo): string => {
  if (info.message === null) {
    return '';
  }
  if (info.traceId === null || info.isKnown) {
    return info.message;
  }
  return `${info.message} (trace: ${info.traceId})`;
};

const isAbortLike = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if ('name' in error && error.name === 'AbortError') {
    return true;
  }
  // RTK Query packages aborted fetches with status === "FETCH_ERROR" and
  // error: "AbortError". Treat both shapes the same.
  if ('error' in error && typeof error.error === 'string' && error.error.includes('AbortError')) {
    return true;
  }
  return false;
};

const EMPTY_API_ERROR: ApiErrorInfo = {
  message: null,
  traceId: null,
  code: null,
  isKnown: false,
};

export const getErrorStatus = (error: unknown): number | string | undefined => {
  if (error !== null && typeof error === 'object' && 'status' in error) {
    return (error as { status: number | string }).status;
  }
  return undefined;
};

export const isAuthError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 401 || status === 403;
};

// Re-export for callers that want to narrow before passing to mapApiError.
export type { FetchError };
